import { RefObject } from "react";

export type CursorTrail = {
  ref: RefObject<HTMLCanvasElement | null>; // ✅ Fix here
  color?: string;
};

export function cursorTrail(props: CursorTrail) {
  const colorRaw = getComputedStyle(document.documentElement).getPropertyValue("--accent");
  const accentColor = `hsla(${colorRaw ? colorRaw.split(" ").join(",") : "0, 0%, 0%"}, 0.35)`;
  const { ref, color } = props;

  if (!ref.current) return { cleanUp: () => {}, renderTrailCursor: () => {} };

  const ctx = ref.current.getContext("2d")!;
  let AnimationFeature = {
    friction: 0.5,
    trails: 20,
    size: 40,
    dampening: 0.2,
    tension: 0.98,
  };

  let cursorPosition = { x: 0, y: 0 };
  let running = true;

  class NewNode {
    x = 0;
    y = 0;
    vx = 0;
    vy = 0;
  }

  class Line {
    spring: number;
    friction: number;
    nodes: NewNode[];

    constructor(spring: number) {
      this.spring = spring + 0.1 * Math.random() - 0.05;
      this.friction = AnimationFeature.friction + 0.01 * Math.random() - 0.005;
      this.nodes = Array.from({ length: AnimationFeature.size }, () => new NewNode());
    }

    update(): void {
      let e = this.spring;
      let t = this.nodes[0];
      t.vx += (cursorPosition.x - t.x) * e;
      t.vy += (cursorPosition.y - t.y) * e;

      for (let i = 0; i < this.nodes.length; i++) {
        t = this.nodes[i];
        if (i > 0) {
          const prev = this.nodes[i - 1];
          t.vx += (prev.x - t.x) * e;
          t.vy += (prev.y - t.y) * e;
          t.vx += prev.vx * AnimationFeature.dampening;
          t.vy += prev.vy * AnimationFeature.dampening;
        }
        t.vx *= this.friction;
        t.vy *= this.friction;
        t.x += t.vx;
        t.y += t.vy;
        e *= AnimationFeature.tension;
      }
    }

    draw(): void {
      ctx.beginPath();
      ctx.moveTo(this.nodes[0].x, this.nodes[0].y);

      for (let i = 1; i < this.nodes.length - 2; i++) {
        const e = this.nodes[i];
        const t = this.nodes[i + 1];
        const midX = 0.5 * (e.x + t.x);
        const midY = 0.5 * (e.y + t.y);
        ctx.quadraticCurveTo(e.x, e.y, midX, midY);
      }

      const secondLast = this.nodes[this.nodes.length - 2];
      const last = this.nodes[this.nodes.length - 1];
      ctx.quadraticCurveTo(secondLast.x, secondLast.y, last.x, last.y);
      ctx.stroke();
      ctx.closePath();
    }
  }

  let newLines: Line[] = [];

  function renderAnimation() {
    if (running) {
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.globalCompositeOperation = "lighter";
      ctx.strokeStyle = color || accentColor;
      ctx.lineWidth = 1;

      newLines.forEach((line) => {
        line.update();
        line.draw();
      });

      requestAnimationFrame(renderAnimation);
    }
  }

  function move(event: MouseEvent | TouchEvent) {
    if (event instanceof MouseEvent) {
      cursorPosition.x = event.clientX;
      cursorPosition.y = event.clientY;
    } else {
      cursorPosition.x = event.touches[0].pageX;
      cursorPosition.y = event.touches[0].pageY;
    }
    event.preventDefault();
  }

  function createLine(event: TouchEvent) {
    if (event.touches.length === 1) {
      cursorPosition.x = event.touches[0].pageX;
      cursorPosition.y = event.touches[0].pageY;
    }
  }

  function onMouseMove(e: MouseEvent | TouchEvent) {
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("touchstart", onMouseMove);
    document.addEventListener("mousemove", move);
    document.addEventListener("touchmove", createLine);
    document.addEventListener("touchstart", createLine);

    function populateLines() {
      newLines = Array.from({ length: AnimationFeature.trails }, (_, i) =>
        new Line(0.45 + (i / AnimationFeature.trails) * 0.025)
      );
    }

    move(e);
    populateLines();
    renderAnimation();
  }

  function resizeCanvas() {
    if (ref.current) {
      ref.current.width = window.innerWidth - 20;
      ref.current.height = window.innerHeight;
    }
  }

  function stopAnimation() {
    running = false;
  }

  function startAnimation() {
    if (!running) {
      running = true;
      renderAnimation();
    }
  }

  function renderTrailCursor() {
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("touchstart", onMouseMove);
    window.addEventListener("orientationchange", resizeCanvas);
    window.addEventListener("resize", resizeCanvas);
    window.addEventListener("focus", startAnimation);
    window.addEventListener("blur", stopAnimation);
    resizeCanvas();
  }

  function cleanUp() {
    document.removeEventListener("mousemove", move);
    document.removeEventListener("touchmove", createLine);
    document.removeEventListener("touchstart", createLine);
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("touchstart", onMouseMove);
    window.removeEventListener("orientationchange", resizeCanvas);
    window.removeEventListener("resize", resizeCanvas);
    window.removeEventListener("focus", startAnimation);
    window.removeEventListener("blur", stopAnimation);
  }

  return { cleanUp, renderTrailCursor, stopAnimation, startAnimation };
}
